// pages/MastersPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Settings, Building2, Users, User, Truck, Container, Plus, Search,
    Edit, Trash2, Eye, Filter, Download, Upload, RefreshCw, MapPin,
    Phone, Mail, Calendar, Star, MoreVertical, Copy, Archive
} from 'lucide-react';
import { Card, Button, Badge, Input, Select, Modal, Table, Pagination } from '../components/ui';

const MastersPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('warehouses');
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [filters, setFilters] = useState({
        status: 'all',
        type: 'all',
        location: 'all'
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Mock data for each master type
    const warehousesData = [
        {
            id: 'WH001',
            name: 'Mumbai Central Warehouse',
            code: 'MCW001',
            type: 'General',
            capacity: 10000,
            currentOccupancy: 7500,
            location: 'Mumbai, Maharashtra',
            address: '123 Industrial Area, Mumbai - 400001',
            manager: 'Rajesh Sharma',
            phone: '+91-9876543210',
            email: 'rajesh@warehouse.com',
            status: 'active',
            rating: 4.3,
            contractValue: 2000000,
            lastTransaction: '2024-03-15T08:45:00',
            totalTransactions: 78,
            performance: 92
        },
        {
            id: 'VEND002',
            name: 'Chennai Logistics Pvt Ltd',
            code: 'CLP001',
            type: 'Logistics Provider',
            category: 'Private',
            services: ['Transportation', 'Warehousing', 'Last Mile'],
            location: 'Chennai, Tamil Nadu',
            address: '123 Logistics Park, Chennai - 600002',
            contactPerson: 'Ravi Shankar',
            phone: '+91-9876543231',
            email: 'ravi@chennailogistics.com',
            status: 'active',
            rating: 4.6,
            contractValue: 1500000,
            lastTransaction: '2024-03-14T17:30:00',
            totalTransactions: 134,
            performance: 88
        }
    ];

    const vehiclesData = [
        {
            id: 'VEH001',
            number: 'MH-01-AB-1234',
            type: 'Truck',
            model: 'Tata 407',
            year: 2021,
            capacity: 25,
            driver: 'Rajesh Kumar',
            driverPhone: '+91-9876543240',
            status: 'active',
            location: 'Mumbai, Maharashtra',
            lastMaintenance: '2024-02-15T00:00:00',
            nextMaintenance: '2024-05-15T00:00:00',
            mileage: 125000,
            fuelType: 'Diesel',
            insurance: {
                provider: 'HDFC ERGO',
                policyNumber: 'POL123456',
                expiryDate: '2024-12-31T00:00:00'
            },
            documents: ['RC', 'Insurance', 'PUC', 'Permit'],
            rating: 4.4
        },
        {
            id: 'VEH002',
            number: 'DL-02-XY-9876',
            type: 'Trailer',
            model: 'Ashok Leyland',
            year: 2020,
            capacity: 40,
            driver: 'Suresh Yadav',
            driverPhone: '+91-9876543241',
            status: 'maintenance',
            location: 'Delhi, Delhi',
            lastMaintenance: '2024-03-01T00:00:00',
            nextMaintenance: '2024-06-01T00:00:00',
            mileage: 145000,
            fuelType: 'Diesel',
            insurance: {
                provider: 'Bajaj Allianz',
                policyNumber: 'POL789012',
                expiryDate: '2024-11-30T00:00:00'
            },
            documents: ['RC', 'Insurance', 'PUC', 'Permit'],
            rating: 4.2
        }
    ];

    const containersData = [
        {
            id: 'CONT001',
            number: 'CONT123456',
            type: '20FT',
            size: '20 Foot',
            owner: 'Maersk Line',
            status: 'available',
            location: 'Mumbai Port',
            condition: 'Good',
            lastInspection: '2024-03-01T00:00:00',
            nextInspection: '2024-06-01T00:00:00',
            weight: 2200,
            maxWeight: 28280,
            dimensions: '20x8x8.5 ft',
            specialFeatures: ['Dry Container'],
            currentJob: null,
            utilizationRate: 85
        },
        {
            id: 'CONT002',
            number: 'CONT789012',
            type: '40FT',
            size: '40 Foot',
            owner: 'CMA CGM',
            status: 'in_use',
            location: 'En Route',
            condition: 'Excellent',
            lastInspection: '2024-02-15T00:00:00',
            nextInspection: '2024-05-15T00:00:00',
            weight: 3800,
            maxWeight: 32500,
            dimensions: '40x8x8.5 ft',
            specialFeatures: ['High Cube', 'Refrigerated'],
            currentJob: 'JOB002',
            utilizationRate: 92
        }
    ];

    const tabs = [
        { id: 'warehouses', label: 'Warehouses', icon: Building2, data: warehousesData },
        { id: 'customers', label: 'Customers', icon: Users, data: customersData },
        { id: 'vendors', label: 'Vendors', icon: User, data: vendorsData },
        { id: 'vehicles', label: 'Vehicles', icon: Truck, data: vehiclesData },
        { id: 'containers', label: 'Containers', icon: Container, data: containersData }
    ];

    const currentTabData = tabs.find(tab => tab.id === activeTab)?.data || [];

    // Filter and search logic
    const filteredData = currentTabData.filter(item => {
        const matchesSearch = !searchQuery ||
            item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.number?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesStatus = filters.status === 'all' || item.status === filters.status;

        return matchesSearch && matchesStatus;
    });

    // Pagination
    const totalPages = Math.ceil(filteredData.length / pageSize);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { variant: 'success', label: 'Active' },
            inactive: { variant: 'gray', label: 'Inactive' },
            maintenance: { variant: 'warning', label: 'Maintenance' },
            available: { variant: 'success', label: 'Available' },
            in_use: { variant: 'primary', label: 'In Use' },
            premium: { variant: 'purple', label: 'Premium' }
        };

        const config = statusConfig[status] || { variant: 'gray', label: status };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const WarehousesTable = () => {
        const columns = [
            {
                header: 'Warehouse Details',
                accessor: 'name',
                render: (_, warehouse) => (
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <div className="flex items-center">
                                <span className="font-medium text-gray-900">{warehouse.name}</span>
                                <div className="flex items-center ml-2">
                                    <Star className="w-4 h-4 text-yellow-400 mr-1" />
                                    <span className="text-sm text-gray-600">{warehouse.rating}</span>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">{warehouse.code}</p>
                        </div>
                    </div>
                )
            },
            {
                header: 'Type & Capacity',
                accessor: 'type',
                render: (_, warehouse) => (
                    <div>
                        <Badge variant="gray">{warehouse.type}</Badge>
                        <p className="text-sm text-gray-600 mt-1">
                            {warehouse.currentOccupancy.toLocaleString()} / {warehouse.capacity.toLocaleString()} sqft
                        </p>
                        <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                            <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${(warehouse.currentOccupancy / warehouse.capacity) * 100}%` }}
                            />
                        </div>
                    </div>
                )
            },
            {
                header: 'Location & Contact',
                accessor: 'location',
                render: (_, warehouse) => (
                    <div>
                        <div className="flex items-center text-sm text-gray-900 mb-1">
                            <MapPin className="w-4 h-4 mr-1" />
                            {warehouse.location}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                            <Phone className="w-4 h-4 mr-1" />
                            {warehouse.phone}
                        </div>
                        <p className="text-sm text-gray-600">{warehouse.manager}</p>
                    </div>
                )
            },
            {
                header: 'Status',
                accessor: 'status',
                render: (_, warehouse) => (
                    <div>
                        {getStatusBadge(warehouse.status)}
                        <p className="text-xs text-gray-500 mt-1">
                            Updated: {new Date(warehouse.lastUpdated).toLocaleDateString()}
                        </p>
                    </div>
                )
            },
            {
                header: 'Actions',
                accessor: 'actions',
                render: (_, warehouse) => (
                    <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <MoreVertical className="w-4 h-4" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Table
                columns={columns}
                data={paginatedData}
                loading={loading}
                emptyMessage="No warehouses found"
            />
        );
    };

    const CustomersTable = () => {
        const columns = [
            {
                header: 'Customer Details',
                accessor: 'name',
                render: (_, customer) => (
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <Users className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <div className="flex items-center">
                                <span className="font-medium text-gray-900">{customer.name}</span>
                                {customer.relationship === 'premium' && (
                                    <Badge variant="purple" size="sm" className="ml-2">Premium</Badge>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">{customer.code} • {customer.industry}</p>
                        </div>
                    </div>
                )
            },
            {
                header: 'Contact & Location',
                accessor: 'location',
                render: (_, customer) => (
                    <div>
                        <div className="flex items-center text-sm text-gray-900 mb-1">
                            <MapPin className="w-4 h-4 mr-1" />
                            {customer.location}
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mb-1">
                            <Phone className="w-4 h-4 mr-1" />
                            {customer.phone}
                        </div>
                        <p className="text-sm text-gray-600">{customer.contactPerson}</p>
                    </div>
                )
            },
            {
                header: 'Financial Info',
                accessor: 'creditLimit',
                render: (_, customer) => (
                    <div>
                        <p className="text-sm font-medium text-gray-900">
                            Credit: ₹{(customer.creditLimit / 100000).toFixed(1)}L
                        </p>
                        <p className="text-sm text-gray-600">
                            Outstanding: ₹{customer.outstandingAmount.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                            Orders: {customer.totalOrders}
                        </p>
                    </div>
                )
            },
            {
                header: 'Status & Rating',
                accessor: 'status',
                render: (_, customer) => (
                    <div>
                        {getStatusBadge(customer.status)}
                        <div className="flex items-center mt-1">
                            <Star className="w-4 h-4 text-yellow-400 mr-1" />
                            <span className="text-sm text-gray-600">{customer.rating}</span>
                        </div>
                    </div>
                )
            },
            {
                header: 'Actions',
                accessor: 'actions',
                render: (_, customer) => (
                    <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <MoreVertical className="w-4 h-4" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Table
                columns={columns}
                data={paginatedData}
                loading={loading}
                emptyMessage="No customers found"
            />
        );
    };

    const VehiclesTable = () => {
        const columns = [
            {
                header: 'Vehicle Details',
                accessor: 'number',
                render: (_, vehicle) => (
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                            <Truck className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                            <span className="font-medium text-gray-900">{vehicle.number}</span>
                            <p className="text-sm text-gray-500">{vehicle.model} ({vehicle.year})</p>
                        </div>
                    </div>
                )
            },
            {
                header: 'Driver & Contact',
                accessor: 'driver',
                render: (_, vehicle) => (
                    <div>
                        <p className="font-medium text-gray-900">{vehicle.driver}</p>
                        <div className="flex items-center text-sm text-gray-600">
                            <Phone className="w-4 h-4 mr-1" />
                            {vehicle.driverPhone}
                        </div>
                    </div>
                )
            },
            {
                header: 'Specifications',
                accessor: 'type',
                render: (_, vehicle) => (
                    <div>
                        <Badge variant="gray">{vehicle.type}</Badge>
                        <p className="text-sm text-gray-600 mt-1">
                            Capacity: {vehicle.capacity}T
                        </p>
                        <p className="text-xs text-gray-500">
                            Mileage: {vehicle.mileage.toLocaleString()} km
                        </p>
                    </div>
                )
            },
            {
                header: 'Maintenance',
                accessor: 'maintenance',
                render: (_, vehicle) => (
                    <div>
                        <p className="text-sm text-gray-900">
                            Last: {new Date(vehicle.lastMaintenance).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">
                            Next: {new Date(vehicle.nextMaintenance).toLocaleDateString()}
                        </p>
                    </div>
                )
            },
            {
                header: 'Status',
                accessor: 'status',
                render: (_, vehicle) => (
                    <div>
                        {getStatusBadge(vehicle.status)}
                        <div className="flex items-center mt-1">
                            <Star className="w-4 h-4 text-yellow-400 mr-1" />
                            <span className="text-sm text-gray-600">{vehicle.rating}</span>
                        </div>
                    </div>
                )
            },
            {
                header: 'Actions',
                accessor: 'actions',
                render: (_, vehicle) => (
                    <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <MoreVertical className="w-4 h-4" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Table
                columns={columns}
                data={paginatedData}
                loading={loading}
                emptyMessage="No vehicles found"
            />
        );
    };

    const ContainersTable = () => {
        const columns = [
            {
                header: 'Container Details',
                accessor: 'number',
                render: (_, container) => (
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                            <Container className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <span className="font-medium text-gray-900">{container.number}</span>
                            <p className="text-sm text-gray-500">{container.size}</p>
                        </div>
                    </div>
                )
            },
            {
                header: 'Owner & Type',
                accessor: 'owner',
                render: (_, container) => (
                    <div>
                        <p className="font-medium text-gray-900">{container.owner}</p>
                        <Badge variant="gray">{container.type}</Badge>
                    </div>
                )
            },
            {
                header: 'Location & Job',
                accessor: 'location',
                render: (_, container) => (
                    <div>
                        <div className="flex items-center text-sm text-gray-900 mb-1">
                            <MapPin className="w-4 h-4 mr-1" />
                            {container.location}
                        </div>
                        {container.currentJob && (
                            <Badge variant="primary" size="sm">{container.currentJob}</Badge>
                        )}
                    </div>
                )
            },
            {
                header: 'Condition & Utilization',
                accessor: 'condition',
                render: (_, container) => (
                    <div>
                        <Badge variant="success" size="sm">{container.condition}</Badge>
                        <p className="text-sm text-gray-600 mt-1">
                            Utilization: {container.utilizationRate}%
                        </p>
                    </div>
                )
            },
            {
                header: 'Status',
                accessor: 'status',
                render: (_, container) => getStatusBadge(container.status)
            },
            {
                header: 'Actions',
                accessor: 'actions',
                render: (_, container) => (
                    <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <MoreVertical className="w-4 h-4" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Table
                columns={columns}
                data={paginatedData}
                loading={loading}
                emptyMessage="No containers found"
            />
        );
    };

    const renderTable = () => {
        switch (activeTab) {
            case 'warehouses': return <WarehousesTable />;
            case 'customers': return <CustomersTable />;
            case 'vendors': return <CustomersTable />; // Similar structure
            case 'vehicles': return <VehiclesTable />;
            case 'containers': return <ContainersTable />;
            default: return <WarehousesTable />;
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <Settings className="w-6 h-6 mr-2 text-blue-600" />
                        Master Data Management
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Manage warehouses, customers, vendors, vehicles, and containers
                    </p>
                </div>

                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                    <Button variant="outline" size="sm">
                        <Upload className="w-4 h-4 mr-2" />
                        Import
                    </Button>
                    <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                    <Button variant="primary" onClick={() => setShowAddModal(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add New
                    </Button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                    {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    setCurrentPage(1);
                                }}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <TabIcon className="w-4 h-4 mr-2" />
                                    {tab.label}
                                    <Badge variant="gray" size="sm" className="ml-2">
                                        {tab.data.length}
                                    </Badge>
                                </div>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Filters and Search */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center gap-4 w-full sm:w-auto">
                    <Input
                        placeholder={`Search ${activeTab}...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={Search}
                        className="w-full sm:w-80"
                    />
                    <Select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        options={[
                            { value: 'all', label: 'All Status' },
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                            { value: 'maintenance', label: 'Maintenance' }
                        ]}
                    />
                </div>

                <div className="flex items-center gap-2">
                    {selectedItems.length > 0 && (
                        <div className="flex items-center gap-2 mr-4">
                            <span className="text-sm text-gray-600">
                                {selectedItems.length} selected
                            </span>
                            <Button variant="outline" size="sm">
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicate
                            </Button>
                            <Button variant="outline" size="sm">
                                <Archive className="w-4 h-4 mr-2" />
                                Archive
                            </Button>
                        </div>
                    )}
                    <Button variant="outline" size="sm">
                        <Filter className="w-4 h-4 mr-2" />
                        More Filters
                    </Button>
                </div>
            </div>

            {/* Data Table */}
            <div className="space-y-4">
                {renderTable()}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    totalItems={filteredData.length}
                />
            </div>

            {/* Add New Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                title={`Add New ${activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}`}
                size="lg"
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Name" placeholder="Enter name" />
                        <Input label="Code" placeholder="Enter code" />
                    </div>
                    <Input label="Location" placeholder="Enter location" />
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Contact Person" placeholder="Enter contact person" />
                        <Input label="Phone" placeholder="Enter phone number" />
                    </div>
                    <Input label="Email" placeholder="Enter email address" />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="outline" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary">
                            Create {activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MastersPage;